import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const HIDE_SELECTORS = ['.eq2-hierarchy-zoom-bar', '.board-clickup-tooltip'];

function buildExportFilename(ext) {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
  return `estrutura-equipe-${stamp}.${ext}`;
}

function getCaptureScale() {
  return Math.min(2, Math.max(1.25, window.devicePixelRatio || 1));
}

function hideChrome(root) {
  const hidden = [];
  if (!root) return hidden;
  HIDE_SELECTORS.forEach((selector) => {
    root.querySelectorAll(selector).forEach((el) => {
      hidden.push({ el, display: el.style.display });
      el.style.display = 'none';
    });
  });
  return hidden;
}

function restoreChrome(hidden) {
  hidden.forEach(({ el, display }) => {
    el.style.display = display;
  });
}

/** Captura a área visível do viewport (.eq2-hierarchy-body), com zoom/pan atuais. */
export async function captureHierarchyViewport(viewportEl) {
  if (!viewportEl) throw new Error('Área da estrutura indisponível.');

  const modalRoot = viewportEl.closest('.eq2-hierarchy-modal') || viewportEl;
  const hidden = hideChrome(modalRoot);

  const width = viewportEl.clientWidth;
  const height = viewportEl.clientHeight;
  if (width < 1 || height < 1) {
    restoreChrome(hidden);
    throw new Error('Área da estrutura indisponível.');
  }

  try {
    await new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });

    return await html2canvas(viewportEl, {
      backgroundColor: '#ffffff',
      scale: getCaptureScale(),
      useCORS: true,
      allowTaint: true,
      logging: false,
      width,
      height,
      x: viewportEl.scrollLeft,
      y: viewportEl.scrollTop,
      scrollX: -window.scrollX,
      scrollY: -window.scrollY,
      windowWidth: document.documentElement.clientWidth,
      windowHeight: document.documentElement.clientHeight,
      onclone: (doc) => {
        const clonedViewport = doc.querySelector('.eq2-hierarchy-body');
        if (clonedViewport) {
          clonedViewport.style.overflow = 'hidden';
        }
        HIDE_SELECTORS.forEach((selector) => {
          doc.querySelectorAll(selector).forEach((el) => {
            el.style.display = 'none';
          });
        });
      },
    });
  } finally {
    restoreChrome(hidden);
  }
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function exportHierarchyPng(viewportEl) {
  const canvas = await captureHierarchyViewport(viewportEl);
  const blob = await new Promise((resolve) => {
    canvas.toBlob((result) => resolve(result), 'image/png');
  });
  if (!blob) throw new Error('Não foi possível gerar a imagem.');
  downloadBlob(blob, buildExportFilename('png'));
}

export async function exportHierarchyPdf(viewportEl) {
  const canvas = await captureHierarchyViewport(viewportEl);
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 6;
  const maxW = pageW - margin * 2;
  const maxH = pageH - margin * 2;
  const imgRatio = canvas.width / canvas.height;

  let w = maxW;
  let h = w / imgRatio;
  if (h > maxH) {
    h = maxH;
    w = h * imgRatio;
  }

  const x = (pageW - w) / 2;
  const y = (pageH - h) / 2;
  const imgData = canvas.toDataURL('image/jpeg', 0.92);

  pdf.addImage(imgData, 'JPEG', x, y, w, h, undefined, 'FAST');
  pdf.save(buildExportFilename('pdf'));
}
